import type {
  AreaKind,
  BuildingKind,
  FacadeMaterial,
  FacadeStyle,
  OsmTags,
  PitchSport,
  RoadClass,
  RoadSpec,
} from '../types.js';
import type { Region } from '../region.js';
import type { PoiGroup } from './poi.js';

/**
 * Tag → geometry rules. Everything the look of the world depends on lives here, so the
 * unit tests can pin it down and a designer can retune the city without touching the
 * generator.
 */

// ---------------------------------------------------------------------------
// Roads
// ---------------------------------------------------------------------------

const HIGHWAY_TO_CLASS: Record<string, RoadClass> = {
  motorway: 'motorway',
  motorway_link: 'motorway',
  trunk: 'trunk',
  trunk_link: 'trunk',
  primary: 'primary',
  primary_link: 'primary',
  secondary: 'secondary',
  secondary_link: 'secondary',
  tertiary: 'tertiary',
  tertiary_link: 'tertiary',
  unclassified: 'residential',
  residential: 'residential',
  living_street: 'residential',
  road: 'residential',
  service: 'service',
  track: 'track',
  pedestrian: 'path',
  footway: 'path',
  path: 'path',
  cycleway: 'path',
  bridleway: 'path',
  steps: 'path',
  corridor: 'path',
};

/**
 * Streets narrower than this are alleys and service yards: no kerb, and therefore no
 * pavement beside them.
 */
export const KERB_MIN_ROAD_WIDTH = 7;

/**
 * The granite between the carriageway and the pavement: how high it stands and how wide its
 * top is. Shared because the car now rides up onto what the renderer draws (DECISIONS.md §93),
 * so the two have to agree on where the stone is and how tall.
 */
export const KERB_HEIGHT = 0.16;
export const KERB_STONE_WIDTH = 0.45;

/**
 * The paved strip outboard of the kerb, in metres.
 *
 * Shared rather than owned by the mesh builder, because the physics has to agree with it:
 * a pavement you can see is a pavement the car has to treat as a pavement, and the two
 * drifting apart would put the kerb the player is looking at in a different place from the
 * one that costs them speed.
 *
 * Width scales with the street rather than being one number for everything wide enough for
 * a kerb. Across the four shipped fixtures that population runs from 8 m (residential, much
 * the commonest) to 21.9 m (a lane-widened Manhattan avenue), so a fixed ratio was the only
 * option that neither vanished at the narrow end nor dwarfed the kerb at the wide one. 0.18
 * puts an 8 m street at 1.44 m — a single-file pavement, which is what most of a European
 * old town has — and Manhattan's widest avenue at 3.94 m.
 */
export const PAVEMENT_WIDTH_RATIO = 0.18;
export const PAVEMENT_MIN_WIDTH = 1.2;
export const PAVEMENT_MAX_WIDTH = 4;

export function pavementWidthFor(roadWidth: number): number {
  if (roadWidth < KERB_MIN_ROAD_WIDTH) return 0;
  return Math.min(
    PAVEMENT_MAX_WIDTH,
    Math.max(PAVEMENT_MIN_WIDTH, roadWidth * PAVEMENT_WIDTH_RATIO),
  );
}

/**
 * Widths are the full carriageway in metres — deliberately a shade wider than reality so
 * that an arcade car with a forgiving turning circle can actually take a corner without
 * clipping a kerb. Speed is a multiplier on the car's top speed; `drivable: false`
 * surfaces are drawn for context but count as off-road if you put a wheel on one.
 *
 * The colours are a neutral grey rather than the blue-grey they used to be, and about a
 * fifth lighter. Once the sun started casting real shadows, a shadowed street in the old
 * blue-grey came out darker than the `landuse=urban` blanket around it, and from the air
 * you could no longer tell where the road went — which for a racing game is the one thing
 * that has to stay legible.
 */
export const ROAD_SPECS: Record<RoadClass, RoadSpec> = {
  motorway: { width: 18, speed: 1.0, color: 0x6c6e72, drivable: true, layer: 7 },
  trunk: { width: 15, speed: 1.0, color: 0x686a6f, drivable: true, layer: 6 },
  primary: { width: 13, speed: 0.98, color: 0x64666b, drivable: true, layer: 5 },
  secondary: { width: 11, speed: 0.95, color: 0x606267, drivable: true, layer: 4 },
  tertiary: { width: 9.5, speed: 0.92, color: 0x5c5e63, drivable: true, layer: 3 },
  residential: { width: 8, speed: 0.86, color: 0x585a5f, drivable: true, layer: 2 },
  service: { width: 5, speed: 0.72, color: 0x53565b, drivable: true, layer: 1 },
  track: { width: 4, speed: 0.55, color: 0x776650, drivable: true, layer: 0 },
  // Paving, a warmer grey than the pavement's: a footway beside a street is the same kind of
  // thing as the pavement, and the tan it used to be read as a dirt track (§93).
  path: { width: 2.6, speed: 0.35, color: 0x948c80, drivable: false, layer: 0 },
};

export function roadClassFor(tags: OsmTags | undefined): RoadClass | null {
  const hw = tags?.highway;
  if (!hw) return null;
  // A highway=* that is explicitly an area (a pedestrian plaza) is not a ribbon.
  if (tags?.area === 'yes') return null;
  return HIGHWAY_TO_CLASS[hw] ?? null;
}

/**
 * Widens a road when it is tagged with more lanes than its class implies, so that a
 * six-lane "secondary" boulevard doesn't render as a country lane.
 */
export function roadWidthFor(cls: RoadClass, tags: OsmTags | undefined): number {
  const spec = ROAD_SPECS[cls];
  const lanes = Number.parseFloat(tags?.lanes ?? '');
  if (!Number.isFinite(lanes) || lanes <= 0) return spec.width;
  const laneWidth = cls === 'motorway' || cls === 'trunk' ? 3.75 : 3.4;
  const fromLanes = lanes * laneWidth + 1.5;
  // Only ever widen; a "lanes=1" residential street still needs room to drive.
  return Math.max(spec.width, Math.min(fromLanes, spec.width * 2));
}

export function isOneway(tags: OsmTags | undefined): boolean {
  const v = tags?.oneway;
  if (v === 'yes' || v === '1' || v === 'true' || v === '-1') return true;
  return tags?.junction === 'roundabout' && v !== 'no';
}

/** Some service ways are car parks and driveways we would rather not route down. */
export function isRoutable(cls: RoadClass, tags: OsmTags | undefined): boolean {
  if (!ROAD_SPECS[cls].drivable) return false;
  if (tags?.access === 'private' || tags?.access === 'no') return false;
  if (cls === 'service' && (tags?.service === 'parking_aisle' || tags?.service === 'driveway')) {
    return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Buildings
// ---------------------------------------------------------------------------

/** Typical storey height in metres, plus a bit of parapet. */
const LEVEL_HEIGHT = 3.2;

const BUILDING_HEIGHTS: Record<string, number> = {
  house: 7,
  detached: 7,
  bungalow: 4.5,
  semidetached_house: 7.5,
  terrace: 9,
  hut: 3,
  shed: 3,
  garage: 3,
  garages: 3.5,
  roof: 4,
  carport: 3,
  kiosk: 3.5,
  residential: 14,
  apartments: 19,
  dormitory: 16,
  hotel: 28,
  commercial: 17,
  office: 26,
  retail: 10,
  supermarket: 9,
  industrial: 11,
  warehouse: 10,
  manufacture: 12,
  civic: 14,
  government: 18,
  public: 15,
  school: 12,
  university: 20,
  college: 16,
  hospital: 22,
  train_station: 15,
  church: 22,
  chapel: 12,
  cathedral: 45,
  synagogue: 18,
  mosque: 18,
  temple: 18,
  castle: 26,
  tower: 34,
  stadium: 24,
  parking: 12,
};

const DEFAULT_BUILDING_HEIGHT = 12;

/** Parses OSM's loose height syntax: "12", "12 m", "12.5m", "40'" (feet). */
export function parseLength(raw: string | undefined): number | null {
  if (!raw) return null;
  const s = raw.trim();
  const feet = /^(\d+(?:\.\d+)?)\s*'/.exec(s);
  if (feet) return Number.parseFloat(feet[1]) * 0.3048;
  const m = /^(-?\d+(?:\.\d+)?)/.exec(s);
  if (!m) return null;
  const n = Number.parseFloat(m[1]);
  if (!Number.isFinite(n)) return null;
  return /ft|feet/i.test(s) ? n * 0.3048 : n;
}

export interface BuildingHeights {
  height: number;
  minHeight: number;
}

/**
 * Whether a `building=*` way should become a solid, collidable block at street level.
 *
 * This matters far more than it looks. OSM tags plenty of things as buildings that are
 * not obstacles on the road:
 *
 * - **`layer` below zero is underground.** 34th Street–Herald Square subway station is
 *   `building=train_station, layer=-1`, and extruding it put a solid block across West
 *   34th Street that a car simply cannot pass. Before this check, a quarter of all road
 *   centreline vertices in the Manhattan fixture fell inside a "building".
 * - **`building:part` is not a building.** It describes a section of one — a tower on a
 *   podium, a differently-clad wing — and is meant to be drawn *instead of* detail on its
 *   parent, not in addition to it. Rendering both gives coplanar walls and a duplicate
 *   obstacle.
 * - **`location=underground`/`underground=yes`** say the same thing as a negative layer.
 */
export function isSolidBuilding(tags: OsmTags | undefined): boolean {
  if (!tags?.building || tags.building === 'no') return false;
  if (tags['building:part'] === 'yes') return false;
  if (tags.location === 'underground' || tags.underground === 'yes') return false;
  const layer = Number.parseInt(tags.layer ?? '0', 10);
  if (Number.isFinite(layer) && layer < 0) return false;
  return true;
}

/**
 * Roads that pass under something. They stay drivable — they are real roads and the
 * router needs them — but whatever is above them must not be treated as sitting on top
 * of the carriageway.
 */
export function isCovered(tags: OsmTags | undefined): boolean {
  if (!tags) return false;
  if (tags.tunnel && tags.tunnel !== 'no') return true;
  if (tags.covered === 'yes') return true;
  const layer = Number.parseInt(tags.layer ?? '0', 10);
  return Number.isFinite(layer) && layer < 0;
}

/**
 * Height priority: an explicit `height`, then `building:levels`, then a plausible guess
 * from the building type. `jitter` (deterministic, from the building id) nudges the
 * guessed heights so a street of untagged houses isn't a perfectly flat terrace.
 */
export function buildingHeightFor(tags: OsmTags | undefined, jitter: number): BuildingHeights {
  const explicit = parseLength(tags?.height);
  const minExplicit = parseLength(tags?.min_height);
  const levels = Number.parseFloat(tags?.['building:levels'] ?? '');
  const minLevel = Number.parseFloat(tags?.['building:min_level'] ?? '');
  const roofLevels = Number.parseFloat(tags?.['roof:levels'] ?? '');

  let height: number;
  if (explicit !== null && explicit > 0) {
    height = explicit;
  } else if (Number.isFinite(levels) && levels > 0) {
    height = levels * LEVEL_HEIGHT + (Number.isFinite(roofLevels) ? roofLevels * 2 : 1.2);
  } else {
    const kind = tags?.building && tags.building !== 'yes' ? tags.building : undefined;
    const base = kind !== undefined ? (BUILDING_HEIGHTS[kind] ?? DEFAULT_BUILDING_HEIGHT) : DEFAULT_BUILDING_HEIGHT;
    // ±22% so a block reads as a skyline rather than a extruded floorplan.
    height = base * (0.78 + jitter * 0.44);
  }

  let minHeight = 0;
  if (minExplicit !== null && minExplicit > 0) minHeight = minExplicit;
  else if (Number.isFinite(minLevel) && minLevel > 0) minHeight = minLevel * LEVEL_HEIGHT;

  height = clamp(height, 2.5, 400);
  minHeight = clamp(minHeight, 0, height - 1);
  return { height: round2(height), minHeight: round2(minHeight) };
}

/** What each `building=*` value means, for the values OSM actually uses in bulk. */
const BUILDING_TAG_KIND: Record<string, BuildingKind> = {
  house: 'house',
  detached: 'house',
  semidetached_house: 'house',
  terrace: 'house',
  bungalow: 'house',
  farm: 'house',
  hut: 'shed',
  shed: 'shed',
  garage: 'shed',
  garages: 'shed',
  carport: 'shed',
  roof: 'shed',
  service: 'shed',
  apartments: 'apartment',
  residential: 'apartment',
  dormitory: 'apartment',
  kiosk: 'shop',
  retail: 'shop',
  supermarket: 'shop',
  commercial: 'office',
  office: 'office',
  hotel: 'hotel',
  tower: 'tower',
  skyscraper: 'tower',
  industrial: 'industrial',
  warehouse: 'industrial',
  manufacture: 'industrial',
  parking: 'parking',
  church: 'church',
  chapel: 'church',
  cathedral: 'church',
  synagogue: 'church',
  mosque: 'church',
  temple: 'church',
  civic: 'civic',
  government: 'civic',
  public: 'civic',
  school: 'civic',
  university: 'civic',
  college: 'civic',
  hospital: 'civic',
  train_station: 'civic',
  castle: 'civic',
  stadium: 'civic',
};

/**
 * Kinds whose silhouette is the whole point of them, so no amount of height changes the
 * answer: a cathedral spire at 80 m is still a cathedral, and a chimney on a works is not
 * a skyscraper.
 */
const HEIGHT_IMMUNE = new Set<BuildingKind>(['church', 'industrial', 'parking']);

/**
 * Kinds a POI cannot argue with. A hospital with a café in the lobby is a hospital, and an
 * office block with a sandwich shop at the bottom is an office — the ground-floor
 * shopfront treatment belongs to streets of *small* buildings, where it is the whole
 * character of the street rather than one unit in a slab.
 */
const POI_PROOF = new Set<BuildingKind>(['civic', 'hotel', 'office', 'shed']);

/** About eighteen storeys. Above this nothing else about a footprint reads from a car. */
const TOWER_HEIGHT = 55;

/**
 * What a building reads as, from its own tags, from the POIs inside it and from its height.
 *
 * The three signals are needed together because each is missing most of the time.
 * `building=*` is `yes` for 57% of Kraków and 79% of Manhattan; POIs are dense in a city
 * centre and absent from a housing estate; and heights are 87% tagged in Manhattan but 6%
 * in Kraków. Taking them in this order — a real tag, then the height where it is decisive,
 * then the POIs, then the height again as a fallback — means each one is only consulted
 * where the ones above it had nothing to say.
 */
export function buildingKindFor(
  tags: OsmTags | undefined,
  poiGroups: PoiGroup[] | undefined,
  height: number,
): BuildingKind {
  const raw = tags?.building;
  const tagged = raw && raw !== 'yes' ? BUILDING_TAG_KIND[raw] : undefined;
  if (tagged && HEIGHT_IMMUNE.has(tagged)) return tagged;
  if (height >= TOWER_HEIGHT) return 'tower';
  if (tagged && POI_PROOF.has(tagged)) return tagged;

  // `office=*` speaks only where `building=*` has not. On a footprint that says nothing but
  // `yes` it is the best evidence available and beats any POI standing inside it. On one
  // already tagged `building=apartments` it means the block *contains* a solicitor, not that
  // it is a solicitor's office — allowing it to override that turned four Kraków tenements
  // into office blocks.
  //
  // It sits outside the POI groups because there is no office group to put it in: a
  // solicitor's practice and a town hall are both institutions, and only one of the two
  // should look like one.
  if (!tagged && tags?.office && tags.office !== 'no') return 'office';

  if (poiGroups?.length) {
    // Same order the groups themselves are ranked in, and for the same reason: what the
    // player sees at eye level from the road wins. Retail beats lodging deliberately — 86
    // of the 153 Kraków footprints with a hotel or a hostel in them *also* have a
    // restaurant or a shop on the ground floor, and on those the street is the shopfront.
    //
    // Worship outranks the lot: a church with a gift shop in the porch is a church. It sits
    // *below* the tower test rather than beside `HEIGHT_IMMUNE` on purpose, because the one
    // building in the four fixtures that is both tall and has a `place_of_worship` inside it
    // is "The Columns" — 70 m of `building=yes` Midtown tower with a congregation renting a
    // room in it. A tagged `building=church` is still height-immune; an inferred one is not.
    if (poiGroups.includes('worship')) return 'church';
    if (poiGroups.includes('fuel')) return 'fuel';
    if (poiGroups.includes('food') || poiGroups.includes('shop')) return 'shop';
    if (poiGroups.includes('lodging')) return 'hotel';
    if (poiGroups.includes('culture') || poiGroups.includes('civic')) return 'civic';
    if (poiGroups.includes('industrial')) return 'industrial';
    if (poiGroups.includes('parking')) return 'parking';
  }

  if (tagged) return tagged;

  // `building=yes` and nothing inside it. Height is all that is left, and the thresholds are
  // deliberately coarse — this is the majority case, and a wrong guess repeated across half
  // a city is much worse than a bland one.
  if (height >= 24) return 'apartment';
  if (height <= 5.5) return 'shed';
  return 'plain';
}

/**
 * Palette by building kind. Hue families rather than exact colours — the look function then
 * shifts lightness deterministically per building so a terrace has texture.
 *
 * The families deliberately overlap: a house and a block of flats in the same city are
 * built of the same plaster, and what separates them on screen is the pitched roof, not a
 * different colour. The two that do stand apart are the cool glass of an office or tower
 * and the shopfront band, because those are the ones that tell you where you are.
 */
const BUILDING_WALLS: Record<BuildingKind, number[]> = {
  house: [0xd6c3ad, 0xc9b7a4, 0xcbb9a2, 0xbfa892],
  apartment: [0xc9b7a4, 0xbda893, 0xc2b3a5, 0xd0bfab],
  shop: [0xd2b9a0, 0xc7a98f, 0xbfb39e, 0xd8c6ac, 0xc0ab9c],
  office: [0xa8b0ba, 0x97a2ae, 0xb5bcc4],
  tower: [0x8e9aa6, 0x9dabb6, 0x7f8b98, 0xa5b2bc],
  hotel: [0xc6bcae, 0xd3c7b6, 0xb9ae9f],
  civic: [0xd8cdbb, 0xcfc2ac],
  church: [0xd9cfc0, 0xc8bba6],
  industrial: [0x9aa0a6, 0x8b9299, 0xa7aeb4],
  parking: [0x9c9a95, 0x8e8c88, 0xa8a6a0],
  fuel: [0xd8d5cf, 0xc9c6c0],
  shed: [0x9aa0a6, 0xa89e90, 0x8f958c],
  plain: [0xc4b6a6, 0xb9ab9c, 0xcfc1b1, 0xada091],
};

/**
 * Roof colours, as a palette per kind rather than one colour each.
 *
 * Two rules, and the first one is not a matter of taste. **The historic kinds must stay
 * warm**, because they are almost the whole city — `plain`, `shop` and `apartment` are
 * 2,203 of Kraków's 2,689 buildings — and a single desaturated grey across all three turns
 * an aerial view into an unreadable mush in which the tarmac and the roofs are the same
 * colour. That costs the player the one thing they have to be able to see, which is where
 * the road goes. A first pass at this file did exactly that and it had to be reverted from
 * a screenshot; the reference aerial in `docs/screenshots/` is what correct looks like.
 *
 * Second, each kind gets several tones. One colour per kind means every tenement on a
 * street has an identical roof, which reads as a texture error rather than as a terrace.
 * The modern kinds — office, tower, industrial, parking — are the deliberate exception to
 * the warmth, because a flat membrane roof really is grey, and that contrast is what makes
 * a post-war block legible next to the old town around it.
 */
const BUILDING_ROOFS: Record<BuildingKind, number[]> = {
  house: [0x8f5b45, 0xa06547, 0x7d5240, 0x996047],
  apartment: [0x9c6650, 0xa87058, 0x8a5c48, 0x7e6355],
  shop: [0x99654e, 0xa26b52, 0x8a5f4c, 0x7d6153],
  hotel: [0x8e6552, 0x9a6c55, 0x7b6153],
  civic: [0x86705c, 0x7d6a5a, 0x93785f],
  // The widest spread of any kind, and deliberately so: `plain` is the "we have no idea"
  // bucket and the largest single group in every city we have looked at. Giving all 1,022
  // of Kraków's the same terracotta is as much a lie as giving them all the same grey, and
  // it makes a district of them read as one continuous roof.
  plain: [0x94614c, 0x8a6250, 0xa06a52, 0x7f6355, 0x6f6863, 0x7c7269, 0x615d59],
  shed: [0x7a6152, 0x8a6a55, 0x6e6156],
  // Verdigris copper. Kraków's church roofs are the one thing you can navigate by from the
  // air, and a grey one disappears into the tenements around it.
  church: [0x4e6a64, 0x456059, 0x577569],
  office: [0x51555a, 0x5b6066, 0x4a4f55],
  tower: [0x464c54, 0x515862, 0x3f454d],
  industrial: [0x6a6f74, 0x5f666b, 0x74797e],
  parking: [0x53514d, 0x5d5b56],
  fuel: [0xb04a3c],
};

/**
 * Ground-floor bands: awning, fascia and glass, always darker than the wall above. Kept
 * dark and low-chroma so that a whole parade of them reads as one continuous shopfront line
 * rather than as a row of individually painted boxes.
 */
const SHOPFRONT_BANDS = [0x3f4a52, 0x4a3f38, 0x36443c, 0x503a3a, 0x44404f];

const ROOF_STYLE: Record<BuildingKind, 'flat' | 'pitched' | 'plant'> = {
  house: 'pitched',
  shed: 'pitched',
  church: 'pitched',
  office: 'plant',
  tower: 'plant',
  industrial: 'plant',
  apartment: 'flat',
  shop: 'flat',
  hotel: 'flat',
  civic: 'flat',
  parking: 'flat',
  fuel: 'flat',
  plain: 'flat',
};

export interface BuildingLook {
  wall: number;
  roof: number;
  /** Ground-floor band, or null where there is no shopfront to draw. */
  base: number | null;
  /** A hint for the mesh builder, not a colour: `plant` means rooftop machinery. */
  roofStyle: 'flat' | 'pitched' | 'plant';
}

export function buildingLookFor(kind: BuildingKind, jitter: number): BuildingLook {
  const walls = BUILDING_WALLS[kind];
  const roofs = BUILDING_ROOFS[kind];
  // Second-order jitter on lightness: ±10%.
  const k = 0.9 + ((jitter * 7.13) % 1) * 0.2;
  return {
    wall: shade(walls[pick(walls.length, jitter)], k),
    // Drawn on its own axis, so a building's roof tone is not welded to its wall tone —
    // a terrace whose walls and roofs vary together reads as a repeated stamp.
    roof: shade(
      roofs[pick(roofs.length, (jitter * 5.31) % 1)],
      0.92 + ((jitter * 2.17) % 1) * 0.16,
    ),
    // A third-order draw, so the band a building gets is not locked to its wall colour —
    // otherwise every cream shopfront on the street ends up with the same green awning.
    base: kind === 'shop' ? SHOPFRONT_BANDS[pick(SHOPFRONT_BANDS.length, (jitter * 3.7) % 1)] : null,
    roofStyle: ROOF_STYLE[kind],
  };
}

// ---------------------------------------------------------------------------
// Facade archetypes
// ---------------------------------------------------------------------------

/**
 * Above this a footprint is a slab, a shed or a supermarket, and never a tenement.
 *
 * 600 m² is a shade over the 90th percentile of footprint area in both European regions we
 * ship — Kraków's Old Town is 602 m² at p90 and the Marais 664 m² — so it keeps essentially
 * the whole historic city on the compact side of the line while putting Midtown's p90 of
 * 1,578 m² firmly on the other.
 */
const PARCEL_MAX_AREA = 600;

/**
 * Corners before a footprint counts as hand-drawn rather than laid out.
 *
 * This is the one era signal in the data that has full coverage, and it is geometric: a
 * parcel that predates the bulldozer is small and many-cornered, because it grew against
 * its neighbours. Measured mean corners per footprint: Kraków 15.9, the Marais 13.7,
 * Midtown 9.2 — the medieval and the 19th-century cities are visibly more articulated than
 * the gridded one. Five is deliberately low: it only has to exclude the pure rectangle.
 */
const PARCEL_MIN_VERTICES = 5;

/** Glass needs a building that is genuinely tall *and* tall for the city it is standing in. */
const CURTAIN_MIN_STOREYS = 10;
const CURTAIN_MIN_RANK = 0.8;

/** A compact footprint this tall in a city where almost nothing is, is a post-war intrusion. */
const MODERN_MIN_STOREYS = 10;
const MODERN_MIN_RANK = 0.95;

/**
 * Storeys before a large footprint reads as a slab rather than as a grand old institution.
 *
 * Absolute, not a rank, and that is the point: rank cannot separate these two: Kraków's
 * palaces sit between rank 0.25 and 0.85 and Midtown's commercial blocks between 0.11 and
 * 0.74, straight through each other. Height in metres does separate them — nobody built a
 * thousand-square-metre plan six storeys high before they could build it taller.
 */
const SLAB_MIN_STOREYS = 6;

/** Where the grander treatment — shutters, a balcony rail, a cornice — starts. */
const ORNATE_MIN_STOREYS = 5;

/** Two storeys and a plain rectangle is a lock-up, a pavilion or a cottage. */
const VERNACULAR_MAX_STOREYS = 2;

/** A tall, tiny footprint is a campanile, not an office tower. */
const SLENDER_TOWER_AREA = 150;

/**
 * How often a `period` terrace throws up a grander plot. Low, because a terrace really is
 * mostly uniform — this is the one rebuilt house in a row of eight, not a coin flip.
 */
const REBUILT_PLOT_CHANCE = 0.12;

export interface FacadeInputs {
  kind: BuildingKind;
  /** Storeys, from the height the generator settled on rather than from any tag. */
  storeys: number;
  /**
   * Where this building sits in its own region's height distribution, 0 to 1.
   *
   * The normalisation is the whole reason this is a rank and not a storey count, and it is
   * not a refinement — an absolute threshold gets two of our three cities backwards. "Four
   * storeys or fewer is old, taller is modern" reads Kraków correctly, where 1,763 of 2,689
   * buildings are 3–4 storeys. It then classifies almost the entire Marais as modern
   * high-rise: 1,543 of its buildings are 5–6 storeys and 946 are 7–9, and they are
   * 19th-century tenements to a building. Midtown has 467 buildings at 5–6 storeys and those
   * are pre-war walk-ups. Height means "old" or "new" only relative to the city around it.
   */
  rank: number;
  /** Footprint area in m². */
  area: number;
  /** Corners on the outer ring. */
  vertices: number;
  jitter: number;
}

/**
 * Which facade archetype a building's walls are drawn from.
 *
 * Deliberately not driven by `start_date`. OSM has the tag and it would answer this question
 * directly, but it is absent from every footprint in all four shipped regions — it only
 * exists in bulk where a national import supplied it — so the era has to be inferred. Two
 * signals do it between them and both have complete coverage: the storey count read against
 * the city's own distribution, and the shape of the parcel.
 *
 * Ordered so that the kinds whose construction *is* their appearance answer first — a works
 * is industrial glazing whatever its height, a church is masonry whatever is inside it — and
 * the generic bucket, which is most of every city, falls through to the geometry.
 */
export function facadeStyleFor(input: FacadeInputs): FacadeStyle {
  const { kind, storeys, rank, area, vertices, jitter } = input;

  // Kinds where the construction is the point, and no amount of height changes it.
  if (kind === 'industrial' || kind === 'parking' || kind === 'fuel') return 'utility';
  if (kind === 'shed') return 'vernacular';
  if (kind === 'house') return 'vernacular';
  if (kind === 'church') return 'ornate';
  if (kind === 'tower') return area < SLENDER_TOWER_AREA ? 'ornate' : 'curtain';
  if (kind === 'office') {
    return storeys >= CURTAIN_MIN_STOREYS && rank >= CURTAIN_MIN_RANK ? 'curtain' : 'modern';
  }

  const compact = area <= PARCEL_MAX_AREA;
  const articulated = vertices >= PARCEL_MIN_VERTICES;

  if (!compact) {
    if (storeys >= CURTAIN_MIN_STOREYS && rank >= CURTAIN_MIN_RANK) return 'curtain';
    // A big footprint two storeys high is a supermarket or a depot, not a mansion.
    if (storeys <= VERNACULAR_MAX_STOREYS) return 'utility';
    // A big plan only reads as a slab once it has the height to go with it. Below that it is
    // a palace, a monastery or a market hall, and an old town is full of them: without this
    // line 236 of Kraków's buildings came out modern, and the sample was the university
    // quarter — 3 to 5 storeys over a thousand square metres, one of them with 126 corners.
    return storeys >= SLAB_MIN_STOREYS ? 'modern' : 'ornate';
  }

  if (storeys >= MODERN_MIN_STOREYS && rank >= MODERN_MIN_RANK) return 'modern';
  // A rectangle has to be two storeys taller to earn the grander treatment: the balconies
  // and cornice belong to a building that was worth articulating in plan as well.
  if (storeys >= (articulated ? ORNATE_MIN_STOREYS : ORNATE_MIN_STOREYS + 2)) return 'ornate';
  if (storeys <= VERNACULAR_MAX_STOREYS && !articulated) return 'vernacular';
  // One plot in eight comes back grander than its neighbours. A terrace is mostly uniform —
  // that is what makes it a terrace — but a row where every single house is identical is the
  // giveaway that it was stamped rather than built.
  return ((jitter * 11.7) % 1) < REBUILT_PLOT_CHANCE ? 'ornate' : 'period';
}

// ---------------------------------------------------------------------------
// Facade materials, and the colours that go with them
// ---------------------------------------------------------------------------

/**
 * What a building's walls are made of.
 *
 * `building:material` answers it where it is mapped, and that is rare — it is stripped from
 * the shipped fixtures and patchy where it exists — so mostly it is a draw weighted by the
 * era and the country. The weights are the point. A 19th-century tenement is rendered and
 * painted in Kraków, red brick in Manhattan, limestone in Paris; a post-war block is a
 * prefabricated panel slab in Poland far more often than anywhere else. Drawn all one way,
 * Kraków, Manhattan and the Marais were the same city with different street plans.
 */
export function facadeMaterialFor(
  style: FacadeStyle,
  kind: BuildingKind,
  region: Region,
  jitter: number,
  tags?: OsmTags,
): FacadeMaterial {
  const mapped = materialFromTag(tags?.['building:material']);
  if (mapped) return mapped;
  if (style === 'curtain') return 'glass';
  // Its own axis, so the material is not welded to the palette pick that also uses `jitter`.
  const roll = (jitter * 13.37) % 1;
  const pick = (weights: [FacadeMaterial, number][]): FacadeMaterial => {
    let total = 0;
    for (const [, w] of weights) total += w;
    let at = roll * total;
    for (const [material, w] of weights) {
      at -= w;
      if (at < 0) return material;
    }
    return weights[weights.length - 1][0];
  };

  // Gothic in brick is what a Polish or north German church is; elsewhere, stone.
  if (kind === 'church') return region === 'pl' || region === 'de' ? pick([['brick', 3], ['stone', 2]]) : 'stone';

  switch (style) {
    case 'period':
      if (region === 'us') return pick([['brick', 65], ['stone', 25], ['render', 10]]);
      if (region === 'gb') return pick([['brick', 70], ['render', 30]]);
      if (region === 'fr') return pick([['stone', 55], ['render', 45]]);
      if (region === 'de') return pick([['render', 65], ['brick', 35]]);
      if (region === 'pl') return pick([['render', 82], ['brick', 18]]);
      return pick([['render', 65], ['brick', 35]]);
    case 'ornate':
      if (region === 'fr') return pick([['stone', 85], ['render', 15]]);
      if (region === 'us') return pick([['stone', 55], ['brick', 45]]);
      if (region === 'gb') return pick([['stone', 50], ['brick', 50]]);
      return pick([['render', 65], ['stone', 35]]);
    case 'modern':
      if (region === 'pl') return pick([['panel', 60], ['render', 30], ['brick', 10]]);
      if (region === 'us') return pick([['brick', 40], ['panel', 40], ['render', 20]]);
      return pick([['panel', 45], ['render', 40], ['brick', 15]]);
    case 'utility':
      return pick([['metal', 50], ['brick', 30], ['panel', 20]]);
    case 'vernacular':
      if (region === 'us' || region === 'gb') return pick([['brick', 55], ['render', 45]]);
      return pick([['render', 75], ['brick', 25]]);
    default:
      return 'render';
  }
}

function materialFromTag(value: string | undefined): FacadeMaterial | null {
  switch (value?.toLowerCase()) {
    case 'brick':
      return 'brick';
    case 'stone':
    case 'sandstone':
    case 'limestone':
    case 'granite':
    case 'marble':
      return 'stone';
    case 'concrete':
    case 'reinforced_concrete':
    case 'panel':
      return 'panel';
    case 'glass':
    case 'mirror':
      return 'glass';
    case 'metal':
    case 'steel':
    case 'aluminium':
    case 'metal_plates':
      return 'metal';
    case 'plaster':
    case 'render':
    case 'wood':
    case 'timber_framing':
      return 'render';
    default:
      return null;
  }
}

/**
 * Render colours by country. A painted tenement is the one wall whose colour is a choice,
 * and the choices are regional: Kraków's are pastels — ochre, cream, salmon, sage, a pale
 * blue — Paris's are all within a few shades of the stone they imitate, and an American
 * stucco wall is a grey or a beige.
 */
const RENDER_WALLS: Record<Region, number[]> = {
  pl: [
    0xe8dcc0, 0xdcc48f, 0xe6d8a4, 0xdcb7a0, 0xc8d2b0, 0xc6d0d4, 0xe6e1d6, 0xbdb6a8, 0xdfc4bc,
    0xd5c3a4, 0xcfd7c4, 0xe0cfae,
  ],
  de: [0xe6e0d2, 0xddd2b8, 0xc9cfc8, 0xe2d3b5, 0xcfc4b4, 0xd8c0a8, 0xd9d4c6],
  fr: [0xe4d8c2, 0xdccfb6, 0xe8dfcf, 0xd3c6ad, 0xcbbfa8],
  gb: [0xe8e4da, 0xd9d2c4, 0xcfc8bb, 0xe2dccd],
  us: [0xd8d0c0, 0xc8c0b0, 0xbfb7a8, 0xd2c6b2],
  plain: [0xdcd2c0, 0xd2c6b2, 0xc9c0b2, 0xe0d6c4, 0xcdc4b4, 0xd6cbb6],
};

/** Brick, which is the same few reds everywhere, and a London stock yellow where it is one. */
const BRICK_WALLS: Record<Region, number[]> = {
  pl: [0x9a4e38, 0x8a4a38, 0xa35b40, 0x7e4636],
  de: [0x9c5a44, 0x8f4f3f, 0xb37b5c, 0x87473a],
  fr: [0xa86048, 0x9a5842],
  gb: [0x8e4a36, 0x9a543c, 0xb5976a, 0x7c4334],
  us: [0x8e4a36, 0x9a543c, 0x7c4334, 0xa45f45, 0x6f4636, 0xb07a5c],
  plain: [0x8e4a36, 0x9a543c, 0x7c4334, 0xa45f45],
};

const STONE_WALLS: Record<Region, number[]> = {
  pl: [0xcfc5b2, 0xbdb3a1, 0xc9bca4],
  de: [0xc9b49a, 0xbfae98, 0xb69a86],
  fr: [0xdcd0b8, 0xd2c5aa, 0xe2d8c4, 0xc8bba0],
  gb: [0xd2c8b4, 0xc6bca8, 0xb7ae9f],
  us: [0xcfc5b2, 0xbdb3a1, 0xa9a59c, 0xc2b49c],
  plain: [0xcfc5b2, 0xbdb3a1, 0xc9bca4, 0xa9a59c],
};

/**
 * Prefab concrete. In Poland most of it has since been insulated and repainted, in exactly
 * the cheerful pale colours that make an estate recognisable from a car; elsewhere it is
 * still mostly concrete grey.
 */
const PANEL_WALLS: Record<Region, number[]> = {
  pl: [0xd9cfb6, 0xc7d3da, 0xd8bfae, 0xcad3b8, 0xe0d8c6, 0xbdbab2, 0xd6c9a0],
  de: [0xd5d1c6, 0xc7c3b9, 0xbcc4c4],
  fr: [0xcfc9bd, 0xc2bdb2],
  gb: [0xbcbab3, 0xc7c3b9, 0xb3b6b5],
  us: [0xbcbab3, 0xc7c3b9, 0xb3b6b5],
  plain: [0xbcbab3, 0xc7c3b9, 0xb3b6b5, 0xcfc9bd],
};

const GLASS_WALLS = [0x8a97a3, 0x7f8c98, 0x6d7d8c, 0x92a0a8, 0x5f6e7c, 0x7b8a86];
const METAL_WALLS = [0x9aa0a6, 0x8b9299, 0xa7aeb4, 0x7c858c, 0x9b8f7e, 0x8a7f74];

/**
 * The wall colour for a material in a country. `building:colour` wins where it is mapped,
 * pulled 40% of the way to a neutral render first: what mappers write is the paint's name,
 * and a literal `yellow` at full strength is a cartoon.
 */
export function wallColorFor(material: FacadeMaterial, region: Region, jitter: number, tags?: OsmTags): number {
  const mapped = parseColour(tags?.['building:colour']);
  if (mapped !== null && material !== 'glass') return mixColor(mapped, 0xd8d2c6, 0.4);
  const palette =
    material === 'brick'
      ? BRICK_WALLS[region]
      : material === 'stone'
        ? STONE_WALLS[region]
        : material === 'panel'
          ? PANEL_WALLS[region]
          : material === 'glass'
            ? GLASS_WALLS
            : material === 'metal'
              ? METAL_WALLS
              : RENDER_WALLS[region];
  // Own axes again, and the same ±10% lightness the kind palettes always had.
  const k = 0.9 + ((jitter * 7.13) % 1) * 0.2;
  return shade(palette[pick(palette.length, (jitter * 17.91) % 1)], k);
}

/** `roof:colour`, where it is mapped, pulled towards a roof-ish neutral the same way. */
export function roofColorFromTags(tags?: OsmTags): number | null {
  const mapped = parseColour(tags?.['roof:colour']);
  return mapped === null ? null : mixColor(mapped, 0x7a6a5e, 0.35);
}

const NAMED_COLOURS: Record<string, number> = {
  white: 0xf2efe8,
  cream: 0xeee2c4,
  beige: 0xd9c8a6,
  yellow: 0xe8d070,
  orange: 0xd99a5a,
  red: 0xa8443a,
  maroon: 0x7a3a34,
  brown: 0x7e5a44,
  pink: 0xe2b4ae,
  green: 0x7f9e6a,
  olive: 0x8a8a5a,
  blue: 0x7f9cc0,
  lightblue: 0xb4c8dc,
  grey: 0x9a9a98,
  gray: 0x9a9a98,
  lightgrey: 0xc4c4c0,
  lightgray: 0xc4c4c0,
  darkgrey: 0x5e5e5c,
  darkgray: 0x5e5e5c,
  black: 0x3a3a3a,
  silver: 0xb8bcc0,
  tan: 0xc8a882,
  sand: 0xd8c8a0,
  ochre: 0xc89a48,
  terracotta: 0xb4624a,
};

/** A hex triple or one of the names OSM actually uses; anything else is not a colour. */
export function parseColour(value: string | undefined): number | null {
  if (!value) return null;
  const v = value.trim().toLowerCase();
  const hex = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/.exec(v);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].replace(/./g, (c) => c + c) : hex[1];
    return Number.parseInt(h, 16);
  }
  return NAMED_COLOURS[v.replace(/[\s_-]/g, '')] ?? null;
}

function mixColor(a: number, b: number, t: number): number {
  const ch = (c: number, shift: number) => (c >> shift) & 0xff;
  const m = (shift: number) => Math.round(ch(a, shift) * (1 - t) + ch(b, shift) * t);
  return (m(16) << 16) | (m(8) << 8) | m(0);
}

/** Storeys from a height in metres, on the same assumption `buildingHeightFor` extrudes with. */
export function storeysOf(height: number): number {
  return Math.max(1, Math.round(height / LEVEL_HEIGHT));
}

/**
 * The wall colour on its own, for callers that only have tags. Left in place because the
 * generator has always asked the question this way; the height defaults to the same guess
 * `buildingHeightFor` would make, so an untagged footprint doesn't come back as a shed.
 */
export function buildingColorFor(
  tags: OsmTags | undefined,
  jitter: number,
  height = DEFAULT_BUILDING_HEIGHT,
): number {
  return buildingLookFor(buildingKindFor(tags, undefined, height), jitter).wall;
}

function pick(length: number, jitter: number): number {
  return Math.min(length - 1, Math.floor(clamp(jitter, 0, 0.999999) * length));
}

// ---------------------------------------------------------------------------
// Nature, water, land use
// ---------------------------------------------------------------------------

export const AREA_COLORS: Record<AreaKind, number> = {
  water: 0x2c6a94,
  // The greens are a touch duller than they look on a palette swatch. With the sun casting
  // real shadows the rest of the city sits in a fairly muted range, and a pure leisure-green
  // park next to it reads as a hole cut in the map rather than as grass. Park and grass are
  // a step lighter than they were, about 9%, after "make the parks more bright" (§93) —
  // still well short of a leisure green.
  park: 0x567e48,
  grass: 0x62874d,
  wood: 0x3d5c37,
  sand: 0xc8b78a,
  pitch: 0x567743,
  paved: 0x6e675e,
  // A shade off the service road beside it, so a car park reads as tarmac without reading
  // as more street to drive down.
  parking: 0x5b5c5e,
  // The `landuse=*` blanket that fills the backland between streets. It used to be a very
  // dark blue-grey, which under sun-and-shadow lighting collapsed into black holes across
  // the aerial view; a warm dark grey reads as the yards and car parks it actually is.
  urban: 0x4b473f,
};

/**
 * How far above the ground plane each kind sits. Coplanar polygons z-fight horribly, and
 * a fixed per-kind offset is cheaper and more predictable than polygon-offset state on
 * hundreds of separate draws. The order also encodes what covers what: a park drawn over
 * a landuse=residential blanket, a pitch over the park.
 */
export const AREA_Y: Record<AreaKind, number> = {
  urban: 0.02,
  // Under everything but the landuse blanket, which is where a car park mapped wrongly does
  // least harm: an underground garage drawn as tarmac is hidden by the park or the square
  // that is really on top of it, in the fixtures baked before `parking=*` was kept.
  parking: 0.03,
  wood: 0.04,
  park: 0.06,
  grass: 0.07,
  // Over the park and the grass, because a pond in a park is drawn inside the park's polygon,
  // not cut out of it. Under them it was never seen: on flat ground the park was drawn over it.
  water: 0.075,
  pitch: 0.08,
  sand: 0.09,
  paved: 0.1,
};

/**
 * Priority matters: `natural=water` beats `landuse=forest` on the rare way tagged both,
 * and the broad `landuse=residential` blanket is last so it never covers a park.
 */
export function areaKindFor(tags: OsmTags | undefined): AreaKind | null {
  if (!tags) return null;
  const { natural, landuse, leisure, waterway, water, highway } = tags;

  // A pedestrianised square is tagged as a highway *area*, not a way. Rynek Główny is
  // one of these, and dropping it would put a hole in the middle of Kraków.
  if (tags.area === 'yes' && (highway === 'pedestrian' || highway === 'footway')) {
    return 'paved';
  }
  if (natural === 'water' || water || waterway === 'riverbank' || waterway === 'dock') {
    return 'water';
  }
  // A town park with trees in it is mapped `leisure=park` + `natural=wood` as often as not —
  // Leszno's Plac Kościuszki is — and it is a park: mown, lit, crossed by paths. Read as a
  // forest it came out the darkest green in the palette under a stand of conifers.
  if (leisure === 'park' || leisure === 'garden') return 'park';
  if (natural === 'wood' || landuse === 'forest') return 'wood';
  if (natural === 'beach' || natural === 'sand' || natural === 'shingle') return 'sand';
  if (natural === 'scrub' || natural === 'heath' || natural === 'grassland') return 'grass';
  if (leisure === 'park' || leisure === 'garden' || leisure === 'nature_reserve') return 'park';
  if (leisure === 'pitch' || leisure === 'golf_course' || leisure === 'track') return 'pitch';
  if (tags.amenity === 'parking' && isSurfaceParking(tags)) return 'parking';
  if (leisure === 'playground' || leisure === 'recreation_ground') return 'grass';
  if (landuse === 'grass' || landuse === 'meadow' || landuse === 'village_green') return 'grass';
  if (landuse === 'cemetery' || landuse === 'allotments' || landuse === 'orchard') return 'grass';
  if (landuse === 'recreation_ground') return 'grass';
  if (
    landuse === 'residential' ||
    landuse === 'commercial' ||
    landuse === 'retail' ||
    landuse === 'industrial' ||
    landuse === 'construction' ||
    landuse === 'railway'
  ) {
    return 'urban';
  }
  return null;
}

/** Water and parks are landmarks; a 12 m² flowerbed is noise. */
export const MIN_AREA_M2: Record<AreaKind, number> = {
  water: 120,
  park: 300,
  grass: 300,
  wood: 400,
  sand: 200,
  pitch: 200,
  paved: 250,
  parking: 150,
  urban: 4000,
};

export function areaColorFor(kind: AreaKind, jitter: number, tags?: OsmTags): number {
  const base = kind === 'pitch' ? pitchColorFor(tags) : AREA_COLORS[kind];
  return shade(base, 0.92 + jitter * 0.16);
}

/**
 * A car park that is on the ground, which is the only kind that should be drawn as tarmac.
 *
 * `parking=*` says where the cars are: an underground garage under a square, a multi-storey
 * that is its own building, a rooftop deck, or bays along the kerb that the street already
 * draws. Untagged is a surface lot, and so is most of what is mapped.
 */
export function isSurfaceParking(tags: OsmTags): boolean {
  const kind = tags.parking;
  if (kind !== undefined && kind !== 'surface') return false;
  if (tags.location === 'underground' || tags.location === 'rooftop') return false;
  const layer = Number.parseInt(tags.layer ?? '0', 10);
  return !(Number.isFinite(layer) && layer !== 0);
}

/**
 * What a pitch is marked for. `sport=*` is a list as often as not — an Orlik is
 * `soccer;basketball;volleyball` — and the first sport whose markings we draw wins, because
 * that is the order the mapper thought of them in. `football` is soccer everywhere these
 * pitches are drawn; the American game is mapped as `american_football`.
 */
export function pitchSportFor(tags: OsmTags | undefined): PitchSport | undefined {
  if (tags?.leisure !== 'pitch' || !tags.sport) return undefined;
  for (const raw of tags.sport.split(';')) {
    const sport = raw.trim().toLowerCase();
    if (sport === 'soccer' || sport === 'football' || sport === 'futsal') return 'soccer';
    if (sport === 'basketball') return 'basketball';
    if (sport === 'tennis' || sport === 'padel') return 'tennis';
    if (sport === 'volleyball' || sport === 'beachvolleyball') return 'volleyball';
    if (sport === 'handball') return 'handball';
  }
  return undefined;
}

/**
 * A pitch is the colour of what it is surfaced with: clay is orange, a running track and
 * many a school court are red tartan, a basketball court is tarmac. Green everywhere was the
 * one colour most of them are not. Where `surface` is missing the sport is the best guess —
 * most Polish tennis courts are clay — and everything else is grass.
 */
function pitchColorFor(tags: OsmTags | undefined): number {
  const surface = tags?.surface?.toLowerCase();
  switch (surface) {
    case 'artificial_turf':
      return 0x4c7d3c;
    case 'clay':
      return 0xa35d3f;
    case 'tartan':
    case 'rubber':
      return 0x9a4a3c;
    case 'asphalt':
    case 'concrete':
    case 'paved':
    case 'acrylic':
      return 0x5f6466;
    case 'sand':
      return 0xc8b78a;
    case 'dirt':
    case 'ground':
    case 'gravel':
    case 'fine_gravel':
      return 0x8a7a5e;
    case 'grass':
      return AREA_COLORS.pitch;
  }
  if (tags?.leisure === 'track') return 0x9a4a3c;
  const sport = pitchSportFor(tags);
  if (sport === 'tennis') return 0xa35d3f;
  if (sport === 'basketball' || sport === 'handball') return 0x5f6466;
  if (sport === 'volleyball' && tags?.sport?.includes('beach')) return 0xc8b78a;
  return AREA_COLORS.pitch;
}

/**
 * `building=roof`: a roof with no walls under it. See `Canopy` for why that is not a building.
 * The same exclusions as `isSolidBuilding`, because a roof underground is still underground.
 */
export function isCanopy(tags: OsmTags | undefined): boolean {
  return tags?.building === 'roof' && isSolidBuilding(tags);
}

/**
 * How high a canopy is, and how deep its edge.
 *
 * A filling station's is the one with a standard: about 4.7 m of clearance for a lorry, under
 * a fascia most of a metre deep that the name goes on. A mapped `height` on one of those is
 * often the fascia's own depth, or a guess at a storey; anything that would bring the
 * underside below a van is taken to be wrong. Every other roof — a carport, a bike shelter, a
 * market's — is a thin slab at about the height of a single storey.
 */
export function canopyHeightsFor(
  tags: OsmTags | undefined,
  jitter: number,
  fuel: boolean,
): { bottom: number; top: number } {
  const explicit = parseLength(tags?.height);
  const minExplicit = parseLength(tags?.min_height);
  const levels = Number.parseFloat(tags?.['building:levels'] ?? '');
  const fascia = fuel ? 0.9 : 0.28;
  let top: number;
  if (explicit !== null && explicit > 0) top = explicit;
  else if (Number.isFinite(levels) && levels > 0) top = levels * LEVEL_HEIGHT + 0.6;
  else top = fuel ? 5.5 + jitter * 0.4 : 3 + jitter * 1.2;
  if (fuel) top = Math.max(top, 5.2);
  top = clamp(top, 2.4, 30);
  let bottom = minExplicit !== null && minExplicit > 0 && minExplicit < top - 0.1 ? minExplicit : top - fascia;
  if (fuel) bottom = Math.max(bottom, 4.2);
  bottom = clamp(bottom, 2.1, top - 0.1);
  return { bottom: round2(bottom), top: round2(top) };
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

export function shade(color: number, k: number): number {
  const r = clamp(Math.round(((color >> 16) & 0xff) * k), 0, 255);
  const g = clamp(Math.round(((color >> 8) & 0xff) * k), 0, 255);
  const b = clamp(Math.round((color & 0xff) * k), 0, 255);
  return (r << 16) | (g << 8) | b;
}

function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
